/**
 * Per-fill registry: defaults, validation, and schematic geometry for each of
 * the five fill families. The closed forms and engine authoring live in
 * @kazam/xsec (epsEffClosedForm / z0ClosedForm / authorCoax) — this file only
 * owns what is UI-specific.
 */
import {
  apolloniusCircle,
  capacitanceClosedForm,
  epsEffClosedForm,
  tandEffClosedForm,
  vacuumImpedance,
  z0ClosedForm,
} from "@kazam/xsec";
import {
  Design,
  Fill,
  FillKind,
  HomogeneousFill,
  RadialFill,
  WedgeFill,
  ShellWedgeFill,
  EccentricFill,
  UNIT_TO_M,
  lastSectorDeg,
  toCoaxSpec,
} from "./fill";
import { dielFromPreset } from "./materials";
import { annularSectorPath, ringPath } from "../lib/svgpath";

export interface ValidationIssue {
  /** Field path, e.g. "a", "layers.1.rOuter". */
  path: string;
  message: string;
  severity: "error" | "info" | "warning";
}

export interface ClosedForm {
  available: boolean;
  /** Explanation when unavailable (combined fill off the matched locus). */
  reason?: string;
  z0?: number;
  epsEff?: number;
  cPerM?: number;
  tandEff?: number;
  /** Vacuum (geometry-only) impedance — always available. */
  z0Vacuum: number;
}

export interface SchematicRegion {
  /** SVG path in metres, rendered with fill-rule="evenodd". */
  path: string;
  er: number;
  /** Label anchor in metres. */
  labelX: number;
  labelY: number;
}

export interface SchematicSpec {
  regions: SchematicRegion[];
  /** Conductor outlines: inner disc + shield circle, in metres. */
  inner: { cx: number; r: number };
  outer: { r: number };
  /** Dashed interface guides (circles or radial lines). */
  guides: ({ type: "circle"; cx: number; r: number } | { type: "line"; x0: number; y0: number; x1: number; y1: number })[];
  halfSpan: number;
}

export interface FillDef<F extends Fill = Fill> {
  kind: F["kind"];
  label: string;
  defaults(): F;
  validate(fill: F, unitName: string): ValidationIssue[];
  schematic(fill: F, unitToM: number): SchematicSpec;
}

const DEG = Math.PI / 180;

function baseValidate(a: number, b: number, unit: string): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!(a > 0)) issues.push({ path: "a", message: "inner radius must be > 0", severity: "error" });
  if (!(b > a)) issues.push({ path: "b", message: `outer radius must exceed a = ${a} ${unit}`, severity: "error" });
  return issues;
}

function dielValidate(prefix: string, d: { er: number; tand: number }): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!(d.er >= 1)) issues.push({ path: `${prefix}.er`, message: "εr must be ≥ 1", severity: "error" });
  if (!(d.tand >= 0)) issues.push({ path: `${prefix}.tand`, message: "tanδ must be ≥ 0", severity: "error" });
  return issues;
}

const homogeneous: FillDef<HomogeneousFill> = {
  kind: "homogeneous",
  label: "Homogeneous",
  defaults: () => ({ kind: "homogeneous", a: 0.5, b: 1.725, diel: dielFromPreset("ptfe") }),
  validate: (f, u) => [...baseValidate(f.a, f.b, u), ...dielValidate("diel", f.diel)],
  schematic: (f, u) => ({
    regions: [{ path: ringPath(0, f.b * u, 0, f.a * u), er: f.diel.er, labelX: 0, labelY: ((f.a + f.b) / 2) * u }],
    inner: { cx: 0, r: f.a * u },
    outer: { r: f.b * u },
    guides: [],
    halfSpan: f.b * u,
  }),
};

const radial: FillDef<RadialFill> = {
  kind: "radial",
  label: "Radial layers",
  defaults: () => ({
    kind: "radial",
    a: 0.5,
    b: 1.0,
    layers: [
      { rOuter: 0.7, diel: dielFromPreset("ptfe") },
      { rOuter: 1.0, diel: dielFromPreset("air") },
    ],
  }),
  validate: (f, u) => {
    const issues = baseValidate(f.a, f.b, u);
    if (f.layers.length < 2) issues.push({ path: "layers", message: "at least 2 layers (use Homogeneous for one)", severity: "error" });
    let rPrev = f.a;
    f.layers.forEach((l, i) => {
      const r = i === f.layers.length - 1 ? f.b : l.rOuter;
      if (!(r > rPrev))
        issues.push({ path: `layers.${i}.rOuter`, message: `radius must exceed ${rPrev} ${u}`, severity: "error" });
      rPrev = r;
      issues.push(...dielValidate(`layers.${i}.diel`, l.diel));
    });
    return issues;
  },
  schematic: (f, u) => {
    const regions: SchematicRegion[] = [];
    const guides: SchematicSpec["guides"] = [];
    let rIn = f.a;
    f.layers.forEach((l, i) => {
      const rOut = i === f.layers.length - 1 ? f.b : l.rOuter;
      regions.push({ path: ringPath(0, rOut * u, 0, rIn * u), er: l.diel.er, labelX: 0, labelY: ((rIn + rOut) / 2) * u });
      if (i < f.layers.length - 1) guides.push({ type: "circle", cx: 0, r: rOut * u });
      rIn = rOut;
    });
    return { regions, inner: { cx: 0, r: f.a * u }, outer: { r: f.b * u }, guides, halfSpan: f.b * u };
  },
};

const wedge: FillDef<WedgeFill> = {
  kind: "wedge",
  label: "Wedge sectors",
  defaults: () => ({
    kind: "wedge",
    a: 0.5,
    b: 1.0,
    sectors: [
      { phiDeg: 90, diel: dielFromPreset("alumina") },
      { phiDeg: 270, diel: dielFromPreset("air") },
    ],
  }),
  validate: (f, u) => {
    const issues = baseValidate(f.a, f.b, u);
    if (f.sectors.length < 2) issues.push({ path: "sectors", message: "at least 2 sectors (use Homogeneous for one)", severity: "error" });
    f.sectors.slice(0, -1).forEach((s, i) => {
      if (!(s.phiDeg > 0)) issues.push({ path: `sectors.${i}.phiDeg`, message: "sector angle must be > 0°", severity: "error" });
    });
    const last = lastSectorDeg(f);
    if (!(last > 0))
      issues.push({ path: `sectors.${f.sectors.length - 1}.phiDeg`, message: `sector angles exceed 360° (last = ${last.toFixed(1)}°)`, severity: "error" });
    f.sectors.forEach((s, i) => issues.push(...dielValidate(`sectors.${i}.diel`, s.diel)));
    return issues;
  },
  schematic: (f, u) => {
    const regions: SchematicRegion[] = [];
    const guides: SchematicSpec["guides"] = [];
    let th = 0;
    f.sectors.forEach((s, i) => {
      const span = (i === f.sectors.length - 1 ? lastSectorDeg(f) : s.phiDeg) * DEG;
      const mid = th + span / 2;
      regions.push({
        path: annularSectorPath(f.a * u, f.b * u, th, th + span),
        er: s.diel.er,
        labelX: Math.cos(mid) * ((f.a + f.b) / 2) * u,
        labelY: Math.sin(mid) * ((f.a + f.b) / 2) * u,
      });
      guides.push({ type: "line", x0: Math.cos(th) * f.a * u, y0: Math.sin(th) * f.a * u, x1: Math.cos(th) * f.b * u, y1: Math.sin(th) * f.b * u });
      th += span;
    });
    return { regions, inner: { cx: 0, r: f.a * u }, outer: { r: f.b * u }, guides, halfSpan: f.b * u };
  },
};

const shellWedge: FillDef<ShellWedgeFill> = {
  kind: "shellWedge",
  label: "Shell + wedge",
  // Defaults sit exactly ON the matched locus: 9.6/2.4 = 4.8/1.2 = 4.
  defaults: () => ({
    kind: "shellWedge",
    a: 0.5,
    c: 0.72,
    b: 1.0,
    phiDeg: 90,
    e1: dielFromPreset("alumina"),
    e2: { er: 2.4, tand: 5e-4 },
    e3: { er: 4.8, tand: 1e-3 },
    e4: { er: 1.2, tand: 1e-4 },
  }),
  validate: (f, u) => {
    const issues = baseValidate(f.a, f.b, u);
    if (!(f.c > f.a && f.c < f.b))
      issues.push({ path: "c", message: `shell radius must satisfy a < c < b`, severity: "error" });
    if (!(f.phiDeg > 0 && f.phiDeg < 360))
      issues.push({ path: "phiDeg", message: "wedge angle must be in (0°, 360°)", severity: "error" });
    (["e1", "e2", "e3", "e4"] as const).forEach((k) => issues.push(...dielValidate(k, f[k])));
    const rho = f.e1.er / f.e2.er;
    const offLocus = Math.abs(rho - f.e3.er / f.e4.er) > 1e-9 * rho;
    issues.push({
      path: "e4.er",
      message: offLocus
        ? "off the matched locus ε1/ε2 = ε3/ε4 — no closed form, numerical only"
        : "on the matched locus ε1/ε2 = ε3/ε4 — closed form available",
      severity: "info",
    });
    return issues;
  },
  schematic: (f, u) => {
    const phi = f.phiDeg * DEG;
    const mk = (r0: number, r1: number, a0: number, a1: number, er: number): SchematicRegion => {
      const mid = (a0 + a1) / 2;
      const rMid = (r0 + r1) / 2;
      return { path: annularSectorPath(r0 * u, r1 * u, a0, a1), er, labelX: Math.cos(mid) * rMid * u, labelY: Math.sin(mid) * rMid * u };
    };
    return {
      regions: [
        mk(f.a, f.c, 0, phi, f.e1.er),
        mk(f.c, f.b, 0, phi, f.e2.er),
        mk(f.a, f.c, phi, 2 * Math.PI, f.e3.er),
        mk(f.c, f.b, phi, 2 * Math.PI, f.e4.er),
      ],
      inner: { cx: 0, r: f.a * u },
      outer: { r: f.b * u },
      guides: [
        { type: "circle", cx: 0, r: f.c * u },
        { type: "line", x0: f.a * u, y0: 0, x1: f.b * u, y1: 0 },
        { type: "line", x0: Math.cos(phi) * f.a * u, y0: Math.sin(phi) * f.a * u, x1: Math.cos(phi) * f.b * u, y1: Math.sin(phi) * f.b * u },
      ],
      halfSpan: f.b * u,
    };
  },
};

const eccentric: FillDef<EccentricFill> = {
  kind: "eccentric",
  label: "Eccentric",
  defaults: () => ({ kind: "eccentric", a: 0.3, b: 1.0, d: 0.25, diel: dielFromPreset("ptfe"), twoLayer: null }),
  validate: (f, u) => {
    const issues = baseValidate(f.a, f.b, u);
    if (!(f.d >= 0 && f.d < f.b - f.a))
      issues.push({ path: "d", message: `offset must satisfy 0 ≤ d < b − a = ${(f.b - f.a).toFixed(3)} ${u}`, severity: "error" });
    else if (f.d > 0.85 * (f.b - f.a))
      issues.push({ path: "d", message: "thin-gap regime: loss model (Wheeler) degrades near d → b − a", severity: "warning" });
    if (f.twoLayer) {
      if (!(f.twoLayer.t > 0 && f.twoLayer.t < 1))
        issues.push({ path: "twoLayer.t", message: "interface fraction t must be in (0, 1)", severity: "error" });
      issues.push(...dielValidate("twoLayer.inner", f.twoLayer.inner), ...dielValidate("twoLayer.outer", f.twoLayer.outer));
    } else {
      issues.push(...dielValidate("diel", f.diel));
    }
    return issues;
  },
  schematic: (f, u) => {
    if (!f.twoLayer) {
      // Label in the wide part of the gap (opposite the offset).
      return {
        regions: [{ path: ringPath(0, f.b * u, f.d * u, f.a * u), er: f.diel.er, labelX: -((f.b + f.d - f.a) / 2) * u, labelY: 0 }],
        inner: { cx: f.d * u, r: f.a * u },
        outer: { r: f.b * u },
        guides: [],
        halfSpan: f.b * u,
      };
    }
    const ap = apolloniusCircle(f.a * u, f.b * u, f.d * u, f.twoLayer.t);
    return {
      regions: [
        { path: ringPath(ap.cx, ap.r, f.d * u, f.a * u), er: f.twoLayer.inner.er, labelX: ((f.d + f.a) * u + ap.cx + ap.r) / 2, labelY: 0 },
        { path: ringPath(0, f.b * u, ap.cx, ap.r), er: f.twoLayer.outer.er, labelX: (ap.cx + ap.r + f.b * u) / 2, labelY: 0 },
      ],
      inner: { cx: f.d * u, r: f.a * u },
      outer: { r: f.b * u },
      guides: [{ type: "circle", cx: ap.cx, r: ap.r }],
      halfSpan: f.b * u,
    };
  },
};

export const FILLS: { [K in FillKind]: FillDef } = {
  homogeneous: homogeneous as FillDef,
  radial: radial as FillDef,
  wedge: wedge as FillDef,
  shellWedge: shellWedge as FillDef,
  eccentric: eccentric as FillDef,
};

export const FILL_ORDER: FillKind[] = ["homogeneous", "radial", "wedge", "shellWedge", "eccentric"];

export function validateDesign(design: Design): ValidationIssue[] {
  const def = FILLS[design.fill.kind];
  const issues = def.validate(design.fill, design.unit);
  if (!(design.conductor.sigma >= 0)) issues.push({ path: "conductor.sigma", message: "conductivity must be ≥ 0", severity: "error" });
  return issues;
}

/** Closed-form reference values for the design (pure TS, instant). */
export function closedForm(design: Design): ClosedForm {
  const spec = toCoaxSpec(design);
  const z0Vacuum = vacuumImpedance(spec);
  const eps = epsEffClosedForm(spec);
  if (eps === null) {
    return { available: false, reason: "no closed form off the matched locus ε1/ε2 = ε3/ε4", z0Vacuum };
  }
  return {
    available: true,
    z0: z0ClosedForm(spec)!,
    epsEff: eps,
    cPerM: capacitanceClosedForm(spec)!,
    tandEff: tandEffClosedForm(spec),
    z0Vacuum,
  };
}

/** Schematic in SI metres for the current design. */
export function schematic(design: Design): SchematicSpec {
  return FILLS[design.fill.kind].schematic(design.fill, UNIT_TO_M[design.unit]);
}
